import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { orderConflictRetryPolicy } from '../../../service/api/conflictRetry';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type { Paths } from '@vs-shop/schemas/openapi/paths';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders/{tokenValue}';
const HTTP_METHOD = 'put';

type RequestBodyOf<Op> = Op extends { requestBody: { content: infer C } } ? C[keyof C] : never;

type UpdateOrderBody = RequestBodyOf<Paths[typeof ENDPOINT][typeof HTTP_METHOD]>;

export type MakeUpdateOrderDeps<OShowType> = {
  readonly orderShow: Schema.Codec<OShowType, unknown>;
};

export const makeUpdateOrder = <OShowType>(deps: MakeUpdateOrderDeps<OShowType>) => {
  const UpdateOrderError = createBackendApiError({
    domain: 'Order',
    endpoint: 'UpdateOrder',
    path: ENDPOINT,
    identifierField: 'orderToken',
  } as const);

  const putOrderFetch = (tokenValue: string, data: UpdateOrderBody) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json', 'application/json')
        .fetch({ params: { tokenValue }, body: data });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'order.token': tokenValue,
        },
      })
    );

  const updateOrder = (tokenValue: string, data: UpdateOrderBody) =>
    putOrderFetch(tokenValue, data).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.orderShow)(response),
          404: (response) =>
            Effect.fail(new UpdateOrderError.NotFoundError({ orderToken: tokenValue, response })),
          422: UpdateOrderError.createViolationError(tokenValue),
          orElse: (response) =>
            Effect.fail(new UpdateOrderError.ApiRequestError({ orderToken: tokenValue, response })),
        })
      ),
      Effect.retry(orderConflictRetryPolicy),
      Effect.tap(() =>
        logApiOperation(
          ApiModule.OrderUpdate,
          'Debug',
          `Successfully updated order delivery details for ${tokenValue}`
        )
      ),
      Effect.withSpan('api.order.update', {
        kind: 'internal',
        attributes: {
          'order.token': tokenValue,
        },
      })
    );

  return { updateOrder, UpdateOrderError };
};
